import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import FavoriteIcon from '@mui/icons-material/Favorite';

import CONFIG from '../../config.js';

const logo = new URL('../assets/logo.jpg', import.meta.url);

const VALUES = [
  {
    icon: <EmojiEventsIcon sx={{ fontSize: 36 }} />,
    title: '专业赛事',
    desc: '为各级象棋赛事提供从报名、编排到成绩公示的全流程数字化管理服务',
  },
  {
    icon: <SchoolIcon sx={{ fontSize: 36 }} />,
    title: '系统教学',
    desc: '联合专业象棋教练，打造涵盖入门到高级的系统化课程体系',
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 36 }} />,
    title: '社区连接',
    desc: '活动相册、赛事直播、新闻资讯，让象棋爱好者们紧密相连',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 36 }} />,
    title: '安全可靠',
    desc: '采用银行级数据加密，微信支付官方接入，保障用户信息和资金安全',
  },
];

const MILESTONES = [
  { year: '平台上线', desc: '中象在线微信小程序正式发布' },
  { year: '赛事服务', desc: '完成首批线下象棋赛事的在线报名与管理' },
  { year: '智慧课堂', desc: '在线直播教学功能上线，服务学校象棋社团' },
  { year: '持续迭代', desc: '不断优化产品功能，提升用户体验' },
];

export default function About() {
  return (
    <Box>
      {/* 顶部 */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1A1A1A 0%, #2D1810 100%)',
          color: '#fff',
          py: { xs: 8, md: 14 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Box
            component="img"
            src={logo}
            alt="中象在线"
            sx={{ width: 80, height: 80, borderRadius: 2, mb: 3 }}
          />
          <Typography variant="h2" sx={{ mb: 3, fontWeight: 700 }}>
            关于{CONFIG.appName}
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: 'grey.400', fontWeight: 400, maxWidth: 650, mx: 'auto', lineHeight: 1.8 }}
          >
            中象在线是一个专注于中国象棋领域的综合服务平台，致力于通过数字化手段推广象棋文化，
            服务象棋爱好者、教练员和赛事组织者。
          </Typography>
        </Container>
      </Box>

      {/* 使命愿景 */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <FavoriteIcon sx={{ color: 'secondary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            我们的使命
          </Typography>
        </Stack>
        <Typography
          variant="h5"
          sx={{
            color: 'text.secondary',
            fontWeight: 400,
            lineHeight: 1.8,
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          让每一位象棋爱好者都能便捷地学棋、练棋、参赛，让中国象棋文化传承与发扬。
        </Typography>
      </Container>

      {/* 核心价值 */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" sx={{ mb: 8, fontWeight: 700 }}>
            平台优势
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
              gap: 4,
            }}
          >
            {VALUES.map((v) => (
              <Box key={v.title} sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {v.icon}
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {v.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {v.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* 发展历程 */}
      <Container maxWidth="sm" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mb: 6 }}>
          <HistoryIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h3" sx={{ fontWeight: 700 }}>发展历程</Typography>
        </Stack>
        <Stack spacing={0}>
          {MILESTONES.map((m, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 3, pb: 4, position: 'relative' }}>
              {/* 时间线 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 20 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                />
                {i < MILESTONES.length - 1 && (
                  <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'grey.200', mt: 1 }} />
                )}
              </Box>
              <Box sx={{ pb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                  {m.year}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {m.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
